import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import ReportsSection from '../components/ReportsSection';
import VisaApplicationFilters from '../components/VisaApplicationFilters';
import VisaApplicationsPoolSection from '../components/VisaApplicationsPoolSection';
import VisaApplicationHistoryModal from '../components/VisaApplicationHistoryModal';
import {
  DEFAULT_VISA_FILTERS,
  ERROR_POOL_FILTERS,
  FINALIZE_POOL_FILTERS,
  filterVisaApplications,
  getApplicantName,
} from '../utils/visaApplicationFilters';
import { formatStatusLabel, getStatusBadgeClass } from '../utils/visaApplicationStatus';
import { getVisaHistoryUrl } from '../utils/visaApplicationHistoryApi';
import VisaNotesSection from '../components/VisaNotesSection';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PATH_TO_TAB = {
  '/airlines-manager/dashboard': 'dashboard',
  '/airlines-manager/visa-applications': 'visa_applications',
  '/airlines-manager/error-pool': 'error_pool',
  '/airlines-manager/finalize-pool': 'finalize_pool',
  '/airlines-manager/reports': 'reports',
};

const ComparisonBadge = ({ diff, percent }) => {
  if (diff === 0) {
    return <span className="text-xs text-gray-400">No change vs last month</span>;
  }

  const isUp = diff > 0;
  return (
    <span className={`text-xs font-medium ${isUp ? 'text-green-600' : 'text-red-600'}`}>
      {isUp ? '↑' : '↓'} {Math.abs(diff)} ({percent > 0 ? '+' : ''}{percent}% vs last month)
    </span>
  );
};

const AirlinesManagerDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const getTabFromPath = () => PATH_TO_TAB[location.pathname] || 'dashboard';
  const [activeTab, setActiveTab] = useState(getTabFromPath());
  const [profile, setProfile] = useState(null);
  const [airline, setAirline] = useState(null);
  const [assignedRoutes, setAssignedRoutes] = useState({ from_countries: [], to_countries: [] });
  const [stats, setStats] = useState(null);
  const [visaApplications, setVisaApplications] = useState([]);
  const [visaFilters, setVisaFilters] = useState(DEFAULT_VISA_FILTERS);
  const [errorPoolFilters, setErrorPoolFilters] = useState(ERROR_POOL_FILTERS);
  const [finalizePoolFilters, setFinalizePoolFilters] = useState(FINALIZE_POOL_FILTERS);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [historyApplication, setHistoryApplication] = useState(null);

  const filteredVisaApplications = useMemo(
    () => filterVisaApplications(visaApplications, visaFilters),
    [visaApplications, visaFilters]
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      const tab = getTabFromPath();
      setActiveTab(tab);
      await fetchDashboardData(false);
      if (tab === 'visa_applications') await fetchVisaApplications();
      if (tab === 'error_pool' || tab === 'finalize_pool') await fetchVisaApplications();
      if (tab === 'reports') await fetchReports();
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    const tab = getTabFromPath();
    if (tab !== activeTab) {
      setActiveTab(tab);
      handleTabData(tab);
    }
  }, [location.pathname]);

  const handleTabData = (tabId) => {
    if (tabId === 'visa_applications') fetchVisaApplications();
    if (tabId === 'error_pool' || tabId === 'finalize_pool') fetchVisaApplications();
    if (tabId === 'reports') fetchReports();
    if (tabId === 'dashboard') fetchDashboardData();
  };

  const fetchReports = async () => {
    setReportLoading(true);
    try {
      const res = await axios.get(`${API_URL}/airlines-manager/reports`);
      setReport(res.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  const fetchDashboardData = async (manageLoading = true) => {
    try {
      const res = await axios.get(`${API_URL}/airlines-manager/dashboard`);
      setProfile(res.data.profile);
      setAirline(res.data.airline);
      setAssignedRoutes(res.data.assignedRoutes || { from_countries: [], to_countries: [] });
      setStats(res.data.stats);
      setError('');
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const fetchVisaApplications = async () => {
    try {
      const res = await axios.get(`${API_URL}/airlines-manager/visa-applications`);
      setVisaApplications(res.data);
      setError('');
    } catch (err) {
      console.error('Failed to fetch visa applications:', err);
      setError(err.response?.data?.error || 'Failed to load visa applications');
    }
  };

  if (loading && activeTab !== 'reports' && activeTab !== 'visa_applications' && activeTab !== 'error_pool' && activeTab !== 'finalize_pool') {
    return (
      <Layout>
        <LoadingSpinner text="Loading dashboard..." />
      </Layout>
    );
  }

  const fromCountries = assignedRoutes.from_countries || [];
  const toCountries = assignedRoutes.to_countries || [];

  const renderComparisonCard = (title, currentValue, comparisonKey) => (
    <div className="card">
      <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">{title}</p>
      <p className="text-2xl font-semibold text-ink mt-1">{currentValue ?? 0}</p>
      {stats?.comparison?.[comparisonKey] && (
        <div className="mt-2">
          <ComparisonBadge
            diff={stats.comparison[comparisonKey].diff}
            percent={stats.comparison[comparisonKey].percent}
          />
        </div>
      )}
      <p className="text-xs text-gray-400 mt-1">
        Last month: {stats?.previousMonth?.[comparisonKey] ?? 0}
      </p>
    </div>
  );

  const renderDashboard = () => (
    <>
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="primary-title">Welcome, {profile?.name || 'Manager'}!</h1>
            <p className="text-gray-500 mt-1">Airlines Manager Dashboard</p>
          </div>
          <div className="airline-id-chip shrink-0">
            Airline ID: {airline?.id ?? '-'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Your Profile</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-xl font-bold text-white shadow-sm">
                {profile?.name?.charAt(0)?.toUpperCase() || 'M'}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{profile?.name}</p>
                <p className="text-sm text-gray-500">{profile?.email}</p>
              </div>
            </div>
            <div className="pt-3 border-t border-gray-100 space-y-2 text-sm">
              <p className="text-gray-600"><span className="font-medium">Phone:</span> {profile?.phone || 'Not provided'}</p>
              <p className="text-gray-600">
                <span className="font-medium">Status:</span>{' '}
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  profile?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {profile?.status}
                </span>
              </p>
              <p className="text-gray-600">
                <span className="font-medium">From Countries:</span>{' '}
                {fromCountries.length > 0 ? fromCountries.join(', ') : 'Not assigned'}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">To Countries:</span>{' '}
                {toCountries.length > 0 ? toCountries.join(', ') : 'Not assigned'}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Airline Details</h3>
          {airline ? (
            <div className="space-y-3">
              <div>
                <p className="font-semibold text-gray-900">{airline.name}</p>
                <p className="text-sm text-gray-500">{airline.iata_number}</p>
              </div>
              <div className="pt-3 border-t border-gray-100 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Country</span><span className="font-medium text-gray-900">{airline.country_name || airline.country}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Currency</span><span className="font-medium text-gray-900">{airline.currency}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="font-medium text-gray-900">{airline.email}</span></div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400">Airline information unavailable</p>
          )}
        </div>
      </div>

      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Monthly Statistics</h2>
        <p className="text-sm text-gray-500">
          {stats?.currentMonth?.label} compared to {stats?.previousMonth?.label}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        {renderComparisonCard('Total Applications', stats?.currentMonth?.total, 'total')}
        {renderComparisonCard('New', stats?.currentMonth?.new, 'new')}
        {renderComparisonCard('Processing', stats?.currentMonth?.processing, 'processing')}
        {renderComparisonCard('Error', stats?.currentMonth?.error, 'error')}
        {renderComparisonCard('Finalized', stats?.currentMonth?.finalize, 'finalize')}
      </div>
    </>
  );

  const renderApplicationDetailModal = () => {
    if (!selectedApplication) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedApplication(null)}>
        <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Application Details</h3>
            <button type="button" onClick={() => setSelectedApplication(null)} className="text-gray-400 hover:text-gray-600">×</button>
          </div>
          <div className="space-y-3 text-sm">
            <div><span className="font-medium text-gray-700">Passenger ID:</span> {selectedApplication.passenger_id}</div>
            <div><span className="font-medium text-gray-700">From:</span> {selectedApplication.from_country_name || selectedApplication.from_country}</div>
            <div><span className="font-medium text-gray-700">To:</span> {selectedApplication.to_country_name || selectedApplication.to_country}</div>
            <div><span className="font-medium text-gray-700">Visa Type:</span> {selectedApplication.visa_type}</div>
            <div><span className="font-medium text-gray-700">Application Status:</span> {formatStatusLabel(selectedApplication.application_status)}</div>
            {selectedApplication.status_description && (
              <div><span className="font-medium text-gray-700">Status Description:</span> {selectedApplication.status_description}</div>
            )}
            <VisaNotesSection application={selectedApplication} role={user?.role} />
            <div>
              <p className="font-medium text-gray-700 mb-2">Application Data</p>
              <pre className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(selectedApplication.customer_visa_application_data, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderErrorPool = () => (
    <>
      {renderApplicationDetailModal()}
      <VisaApplicationsPoolSection
        title="Error Pool"
        subtitle="Visa applications with error status on your assigned routes"
        applications={visaApplications}
        filters={errorPoolFilters}
        onFiltersChange={setErrorPoolFilters}
        onClearFilters={() => setErrorPoolFilters(ERROR_POOL_FILTERS)}
        onView={setSelectedApplication}
        onViewHistory={setHistoryApplication}
        emptyMessage={
          fromCountries.length === 0 || toCountries.length === 0
            ? 'No from/to countries assigned. Contact your airline admin.'
            : 'No error applications in your pool.'
        }
        headerExtra={
          <div className="airline-id-chip shrink-0">
            Airline ID: {airline?.id ?? '-'}
          </div>
        }
      />
    </>
  );

  const renderFinalizePool = () => (
    <>
      {renderApplicationDetailModal()}
      <VisaApplicationsPoolSection
        title="Finalize Pool"
        subtitle="Finalized visa applications on your assigned routes"
        applications={visaApplications}
        filters={finalizePoolFilters}
        onFiltersChange={setFinalizePoolFilters}
        onClearFilters={() => setFinalizePoolFilters(FINALIZE_POOL_FILTERS)}
        onView={setSelectedApplication}
        onViewHistory={setHistoryApplication}
        emptyMessage={
          fromCountries.length === 0 || toCountries.length === 0
            ? 'No from/to countries assigned. Contact your airline admin.'
            : 'No finalized applications in your pool.'
        }
        headerExtra={
          <div className="airline-id-chip shrink-0">
            Airline ID: {airline?.id ?? '-'}
          </div>
        }
      />
    </>
  );

  const renderVisaApplications = () => {
    const detailModal = selectedApplication ? (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedApplication(null)}>
        <div className="bg-white rounded-2xl p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Application Details</h3>
            <button type="button" onClick={() => setSelectedApplication(null)} className="text-gray-400 hover:text-gray-600">×</button>
          </div>
          <div className="space-y-3 text-sm">
            <div><span className="font-medium text-gray-700">Passenger ID:</span> {selectedApplication.passenger_id}</div>
            <div><span className="font-medium text-gray-700">From:</span> {selectedApplication.from_country_name || selectedApplication.from_country}</div>
            <div><span className="font-medium text-gray-700">To:</span> {selectedApplication.to_country_name || selectedApplication.to_country}</div>
            <div><span className="font-medium text-gray-700">Visa Type:</span> {selectedApplication.visa_type}</div>
            <div><span className="font-medium text-gray-700">Application Status:</span> {formatStatusLabel(selectedApplication.application_status)}</div>
            {selectedApplication.status_description && (
              <div><span className="font-medium text-gray-700">Status Description:</span> {selectedApplication.status_description}</div>
            )}
            <VisaNotesSection application={selectedApplication} role={user?.role} />
            <div>
              <p className="font-medium text-gray-700 mb-2">Application Data</p>
              <pre className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(selectedApplication.customer_visa_application_data, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    ) : null;

    return (
      <>
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="primary-title">Visa Applications</h1>
              <p className="text-gray-500 mt-1">Applications matching your assigned from and to countries</p>
            </div>
            <div className="airline-id-chip shrink-0">
              Airline ID: {airline?.id ?? '-'}
            </div>
          </div>
        </div>

        {detailModal}

        <VisaApplicationFilters
          applications={visaApplications}
          filters={visaFilters}
          onChange={setVisaFilters}
          onClear={() => setVisaFilters(DEFAULT_VISA_FILTERS)}
          totalCount={visaApplications.length}
          filteredCount={filteredVisaApplications.length}
        />

        <div className="table-shell">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Passenger ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Applicant</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">From</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">To</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Visa Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Submitted</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visaApplications.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-gray-400">
                      {fromCountries.length === 0 || toCountries.length === 0
                        ? 'No from/to countries assigned. Contact your airline admin.'
                        : 'No visa applications found for your assigned routes.'}
                    </td>
                  </tr>
                ) : filteredVisaApplications.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-8 text-gray-400">No applications match your filters.</td>
                  </tr>
                ) : (
                  filteredVisaApplications.map((app) => (
                    <tr key={app.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">{app.passenger_id}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{getApplicantName(app.customer_visa_application_data)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.from_country_name || app.from_country}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.to_country_name || app.to_country}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{app.visa_type}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(app.application_status)}`}>
                          {formatStatusLabel(app.application_status)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-500">{new Date(app.created_at).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedApplication(app)}
                            className="text-sm px-3 py-1.5 rounded-lg font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => setHistoryApplication(app)}
                            className="text-sm px-3 py-1.5 rounded-lg font-medium bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors"
                          >
                            History
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  const renderReports = () => (
    <ReportsSection
      title="Route Reports"
      subtitle="Analytics for visa applications on your assigned routes"
      report={report}
      loading={reportLoading}
    />
  );

  return (
    <Layout>
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'visa_applications' && renderVisaApplications()}
      {activeTab === 'error_pool' && renderErrorPool()}
      {activeTab === 'finalize_pool' && renderFinalizePool()}
      {activeTab === 'reports' && renderReports()}

      {historyApplication && (
        <VisaApplicationHistoryModal
          application={historyApplication}
          historyUrl={getVisaHistoryUrl(historyApplication.id, user?.role)}
          onClose={() => setHistoryApplication(null)}
        />
      )}
    </Layout>
  );
};

export default AirlinesManagerDashboard;
